import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';

const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parseDateOnlyUtc = (value) => {
    const normalized = String(value || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
    const [year, month, day] = normalized.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const dateToKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const shiftUtcDateByDays = (date, days) => {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
};

const nightsBetween = (start, end) => {
    if (!start || !end || end <= start) return 0;
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
};

const formatDate = (value) => {
    const parsed = parseDateOnlyUtc(value);
    if (!parsed) return '-';
    return parsed.toLocaleDateString();
};

const formatCurrency = (value) => {
    return `$${(Number(value) || 0).toFixed(2)}`;
};

const PropertyBookingsCalendarPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [selectedDayKey, setSelectedDayKey] = useState('');

    const normalizedYear = useMemo(() => {
        const parsed = Number(calendarYear);
        if (Number.isInteger(parsed) && parsed >= 1900 && parsed <= 3000) return parsed;
        return new Date().getFullYear();
    }, [calendarYear]);

    const loadProperty = async () => {
        const response = await api.get('/property');
        if (response?.data) {
            const currentProperty = response.data.find((item) => item.id === parseInt(propertyId, 10));
            setProperty(currentProperty || null);
        }
    };

    const loadBookings = async () => {
        if (!propertyId) return;
        setLoading(true);
        try {
            const response = await api.get(`/booking/property/${propertyId}`);
            setBookings(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            console.error('failed to load property bookings', error);
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        if (propertyId) {
            loadProperty();
            loadBookings();
        }
    }, [propertyId]);

    const dayStatsByKey = useMemo(() => {
        const stats = new Map();
        const yearStart = new Date(normalizedYear, 0, 1);
        const yearEnd = new Date(normalizedYear + 1, 0, 1);

        bookings.forEach((booking) => {
            const start = parseDateOnlyUtc(booking.startDate);
            const end = parseDateOnlyUtc(booking.endDate);
            if (!start || !end || end <= start) return;

            if (end <= yearStart || start >= yearEnd) return;

            const totalNights = nightsBetween(start, end);
            const amountPerNight = totalNights > 0 ? (Number(booking.amount) || 0) / totalNights : 0;
            const cursor = new Date(start > yearStart ? start : yearStart);
            const rangeEnd = end < yearEnd ? end : yearEnd;

            while (cursor < rangeEnd) {
                const key = dateToKey(cursor);
                const existing = stats.get(key) || {
                    bookingIds: new Set(),
                    bookings: [],
                    bookedNights: 0,
                    totalRevenue: 0
                };

                existing.bookedNights += 1;
                existing.totalRevenue += amountPerNight;
                if (!existing.bookingIds.has(booking.id)) {
                    existing.bookingIds.add(booking.id);
                    existing.bookings.push({
                        ...booking,
                        amountPerNight
                    });
                }
                stats.set(key, existing);
                cursor.setDate(cursor.getDate() + 1);
            }
        });

        return stats;
    }, [bookings, normalizedYear]);

    const bookedByMonth = useMemo(() => {
        const monthSets = Array.from({ length: 12 }, () => new Set());
        dayStatsByKey.forEach((_, key) => {
            const date = parseDateOnlyUtc(key);
            if (!date) return;
            monthSets[date.getMonth()].add(date.getDate());
        });
        return monthSets;
    }, [dayStatsByKey]);

    const bookingDaysById = useMemo(() => {
        const map = new Map();
        const yearStart = new Date(normalizedYear, 0, 1);
        const yearEnd = new Date(normalizedYear + 1, 0, 1);

        bookings.forEach((booking) => {
            const bookingId = Number(booking.id);
            const start = parseDateOnlyUtc(booking.startDate);
            const end = parseDateOnlyUtc(booking.endDate);
            if (!Number.isInteger(bookingId) || !start || !end || end <= start) return;
            if (end <= yearStart || start >= yearEnd) return;

            const set = map.get(bookingId) || new Set();
            const cursor = new Date(start > yearStart ? start : yearStart);
            const rangeEnd = end < yearEnd ? end : yearEnd;

            while (cursor < rangeEnd) {
                set.add(dateToKey(cursor));
                cursor.setDate(cursor.getDate() + 1);
            }

            map.set(bookingId, set);
        });

        return map;
    }, [bookings, normalizedYear]);

    const bookingChainByDayKey = useMemo(() => {
        const map = new Map();

        dayStatsByKey.forEach((stats, dayKey) => {
            const sorted = (stats?.bookings || [])
                .slice()
                .sort((a, b) => {
                    const startCmp = String(a.startDate || '').localeCompare(String(b.startDate || ''));
                    if (startCmp !== 0) return startCmp;
                    return Number(a.id || 0) - Number(b.id || 0);
                });

            const primary = sorted[0];
            if (!primary) return;

            const bookingSet = bookingDaysById.get(Number(primary.id));
            const dayDate = parseDateOnlyUtc(dayKey);
            if (!bookingSet || !dayDate) return;

            const prevKey = dateToKey(shiftUtcDateByDays(dayDate, -1));
            const nextKey = dateToKey(shiftUtcDateByDays(dayDate, 1));
            map.set(dayKey, {
                hasPrev: bookingSet.has(prevKey),
                hasNext: bookingSet.has(nextKey)
            });
        });

        return map;
    }, [dayStatsByKey, bookingDaysById]);

    const bookedDayKeys = useMemo(() => {
        return new Set(dayStatsByKey.keys());
    }, [dayStatsByKey]);

    const bookingsTouchingYear = useMemo(() => {
        const yearStart = new Date(normalizedYear, 0, 1);
        const yearEnd = new Date(normalizedYear + 1, 0, 1);
        return bookings.filter((booking) => {
            const start = parseDateOnlyUtc(booking.startDate);
            const end = parseDateOnlyUtc(booking.endDate);
            return !!start && !!end && end > start && end > yearStart && start < yearEnd;
        });
    }, [bookings, normalizedYear]);

    const bookedNightsInYear = useMemo(() => bookedDayKeys.size, [bookedDayKeys]);

    const occupancyWindow = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const isCurrentYearView = normalizedYear === currentYear;

        const yearStart = new Date(normalizedYear, 0, 1);
        const acquisitionDate = parseDateOnlyUtc(property?.acquisitionDate);
        const start = acquisitionDate && acquisitionDate > yearStart ? acquisitionDate : yearStart;
        const end = isCurrentYearView
            ? new Date(currentYear, now.getMonth(), now.getDate() + 1)
            : new Date(normalizedYear + 1, 0, 1);

        const days = Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
        return { start, end, days };
    }, [normalizedYear, property?.acquisitionDate]);

    const bookedNightsInOccupancyWindow = useMemo(() => {
        let count = 0;
        bookedDayKeys.forEach((dayKey) => {
            const day = parseDateOnlyUtc(dayKey);
            if (!day) return;
            if (day >= occupancyWindow.start && day < occupancyWindow.end) {
                count += 1;
            }
        });
        return count;
    }, [bookedDayKeys, occupancyWindow]);

    const occupancyRate = useMemo(() => {
        if (!occupancyWindow.days) return 0;
        return (bookedNightsInOccupancyWindow / occupancyWindow.days) * 100;
    }, [bookedNightsInOccupancyWindow, occupancyWindow]);

    const maxDailyRevenue = useMemo(() => {
        let max = 0;
        dayStatsByKey.forEach((entry) => {
            if ((Number(entry.totalRevenue) || 0) > max) {
                max = Number(entry.totalRevenue) || 0;
            }
        });
        return max;
    }, [dayStatsByKey]);

    const selectedDay = useMemo(() => {
        if (!selectedDayKey) return null;
        const stats = dayStatsByKey.get(selectedDayKey);
        if (!stats) return null;
        return {
            key: selectedDayKey,
            stats
        };
    }, [selectedDayKey, dayStatsByKey]);

    const totalNightsAllTime = useMemo(() => {
        return bookings.reduce((sum, booking) => {
            const start = parseDateOnlyUtc(booking.startDate);
            const end = parseDateOnlyUtc(booking.endDate);
            return sum + nightsBetween(start, end);
        }, 0);
    }, [bookings]);

    const totalBookingAmountAllTime = useMemo(() => {
        return bookings.reduce((sum, booking) => sum + (Number(booking.amount) || 0), 0);
    }, [bookings]);

    const averageBookingNightAmount = useMemo(() => {
        if (!totalNightsAllTime) return 0;
        return totalBookingAmountAllTime / totalNightsAllTime;
    }, [totalBookingAmountAllTime, totalNightsAllTime]);

    const previousYear = () => setCalendarYear((current) => Number(current || normalizedYear) - 1);
    const nextYear = () => setCalendarYear((current) => Number(current || normalizedYear) + 1);

    return (
        <div className="property-pnl-page property-bookings-calendar-page">
            <div className="property-pnl-toolbar">
                <button className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>
                    ← Back to Property Details
                </button>
                <div className="property-pnl-title-block">
                    <h2>{property?.name || 'Property'} Bookings Calendar</h2>
                    <p>{property ? `${property.address1}, ${property.city}, ${property.state}` : 'Loading property...'}</p>
                </div>
                <div className="pnl-controls bookings-calendar-controls">
                    <button type="button" className="btn-secondary" onClick={previousYear}>Prev</button>
                    <label htmlFor="calendarYearPage">Year</label>
                    <input
                        id="calendarYearPage"
                        type="number"
                        value={calendarYear}
                        onChange={(event) => setCalendarYear(event.target.value)}
                    />
                    <button type="button" className="btn-secondary" onClick={nextYear}>Next</button>
                </div>
            </div>

            <div className="investment-summary-grid utility-summary-grid">
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Booked Nights ({normalizedYear})</span>
                    <strong>{bookedNightsInYear}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Bookings Touching {normalizedYear}</span>
                    <strong>{bookingsTouchingYear.length}</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Occupancy ({normalizedYear})</span>
                    <strong>{occupancyRate.toFixed(1)}%</strong>
                </div>
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Avg Booking/Night (All-Time)</span>
                    <strong>{formatCurrency(averageBookingNightAmount)}</strong>
                </div>
            </div>

            <div className="property-pnl-card">
                {loading ? (
                    <p className="no-features">Loading bookings...</p>
                ) : (
                    <>
                        <div className="bookings-calendar-legend">
                            <span className="calendar-dot calendar-dot-booked" />
                            <span>Booked night</span>
                            <span className="calendar-legend-divider">|</span>
                            <span className="calendar-legend-intensity calendar-legend-intensity-low">Lower nightly revenue</span>
                            <span className="calendar-legend-intensity calendar-legend-intensity-high">Higher nightly revenue</span>
                        </div>
                        <div className="bookings-calendar-year-grid">
                            {MONTH_LABELS.map((monthLabel, monthIndex) => {
                                const firstOfMonth = new Date(normalizedYear, monthIndex, 1);
                                const firstWeekday = firstOfMonth.getDay();
                                const daysInMonth = new Date(normalizedYear, monthIndex + 1, 0).getDate();
                                const bookedDays = bookedByMonth[monthIndex];
                                const blanks = Array.from({ length: firstWeekday }, (_, idx) => (
                                    <span key={`blank-${monthIndex}-${idx}`} className="calendar-day-cell calendar-day-empty" />
                                ));
                                const dayCells = Array.from({ length: daysInMonth }, (_, dayIdx) => {
                                    const dayNumber = dayIdx + 1;
                                    const dayDate = new Date(normalizedYear, monthIndex, dayNumber);
                                    const dayKey = dateToKey(dayDate);
                                    const isToday = dayKey === dateToKey(new Date());
                                    const isBooked = bookedDays.has(dayNumber);
                                    const dayStats = dayStatsByKey.get(dayKey);
                                    const revenueRatio = maxDailyRevenue > 0 ? Math.min(1, (Number(dayStats?.totalRevenue) || 0) / maxDailyRevenue) : 0;
                                    const intensityClass = revenueRatio >= 0.67
                                        ? ' calendar-day-booked-high'
                                        : revenueRatio >= 0.34
                                            ? ' calendar-day-booked-mid'
                                            : ' calendar-day-booked-low';
                                    const linkColor = revenueRatio >= 0.67 ? '#166534' : revenueRatio >= 0.34 ? '#22c55e' : '#4ade80';
                                    const isSelected = selectedDayKey === dayKey;
                                    const dayChain = bookingChainByDayKey.get(dayKey);
                                    const weekday = dayDate.getDay();
                                    const hasLinkLeft = !!(dayChain?.hasPrev && weekday !== 0);
                                    const hasLinkRight = !!(dayChain?.hasNext && weekday !== 6);
                                    const dayClassName = `calendar-day-cell${isBooked ? ` calendar-day-booked${intensityClass}` : ''}${hasLinkLeft ? ' calendar-booking-link-left' : ''}${hasLinkRight ? ' calendar-booking-link-right' : ''}${isToday ? ' calendar-day-today' : ''}${isSelected ? ' calendar-day-selected' : ''}`;
                                    const title = isBooked
                                        ? `${dayStats.bookings.length} booking(s), $${(Number(dayStats.totalRevenue) || 0).toFixed(2)} total nightly revenue`
                                        : 'Not booked';

                                    return (
                                        <button
                                            key={`day-${normalizedYear}-${monthIndex}-${dayNumber}`}
                                            type="button"
                                            className={`${dayClassName} calendar-day-button`}
                                            onClick={() => setSelectedDayKey(dayKey)}
                                            title={title}
                                            style={isBooked ? { '--booking-link-color': linkColor } : undefined}
                                        >
                                            {dayNumber}
                                        </button>
                                    );
                                });

                                return (
                                    <article key={`${normalizedYear}-${monthIndex}`} className="bookings-calendar-month-card">
                                        <h3>{monthLabel}</h3>
                                        <div className="calendar-weekday-row">
                                            {WEEKDAY_LABELS.map((dayLabel) => (
                                                <span key={`${monthLabel}-${dayLabel}`} className="calendar-weekday-cell">{dayLabel}</span>
                                            ))}
                                        </div>
                                        <div className="calendar-day-grid">
                                            {blanks}
                                            {dayCells}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {selectedDay && (
                            <div className="selected-day-panel">
                                <h4>{formatDate(selectedDay.key)} Details</h4>
                                <p>
                                    <strong>Booked Nights:</strong> {selectedDay.stats.bookedNights} | <strong>Nightly Revenue:</strong> ${Number(selectedDay.stats.totalRevenue || 0).toFixed(2)}
                                </p>
                                <div className="property-pnl-table-wrap">
                                    <table className="utilities-table property-investment-table">
                                        <thead>
                                            <tr>
                                                <th>Check-in</th>
                                                <th>Check-out</th>
                                                <th>Amount</th>
                                                <th>Per Night</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedDay.stats.bookings
                                                .slice()
                                                .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''))
                                                .map((booking) => (
                                                    <tr key={`selected-day-${selectedDay.key}-${booking.id}`}>
                                                        <td>{formatDate(booking.startDate)}</td>
                                                        <td>{formatDate(booking.endDate)}</td>
                                                        <td>${(Number(booking.amount) || 0).toFixed(2)}</td>
                                                        <td>${(Number(booking.amountPerNight) || 0).toFixed(2)}</td>
                                                        <td>{booking.description || '-'}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="property-pnl-card">
                <h3>Bookings ({normalizedYear})</h3>
                {bookingsTouchingYear.length === 0 ? (
                    <p className="no-features">No bookings overlap this year.</p>
                ) : (
                    <div className="property-pnl-table-wrap">
                        <table className="utilities-table property-investment-table">
                            <thead>
                                <tr>
                                    <th>Check-in</th>
                                    <th>Check-out</th>
                                    <th>Nights</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookingsTouchingYear
                                    .slice()
                                    .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''))
                                    .map((booking) => {
                                        const start = parseDateOnlyUtc(booking.startDate);
                                        const end = parseDateOnlyUtc(booking.endDate);
                                        const nights = nightsBetween(start, end);
                                        return (
                                            <tr key={booking.id}>
                                                <td>{formatDate(booking.startDate)}</td>
                                                <td>{formatDate(booking.endDate)}</td>
                                                <td>{nights}</td>
                                                <td>${(Number(booking.amount) || 0).toFixed(2)}</td>
                                                <td>{booking.description || '-'}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PropertyBookingsCalendarPage;
